<script type="text/jsx" lang="jsx">
import _ from 'lodash';
import { mapGetters } from 'vuex';
import PartnerOfferDetailDialog from '@/views/config/partner-refer/dialog/offer-detail';
import { ServiceConfirm } from '@/components/common/service-dialog/confirm';
import minusIconUrl from '@/assets/partner/minus-logo.svg';
import trackingMixin from '@/views/config/partner-refer/tracking/mx';
import { getMessageTemplateByRules } from '@/views/config/partner-refer/helper/validator';
import { isEmptyVal } from '@/utils';
import { couponFetchStatus } from '@/views/config/coupon/helper/constant';
import { logger } from '@/utils/log';

export default {
  name: 'PartnerReferAlert',
  mixins: [trackingMixin],
  created() {
    this.partnerOfferDetailDialog = new ServiceConfirm(PartnerOfferDetailDialog);
    this.contentNode = null;
  },
  computed: {
    ...mapGetters(['isFromPartnerRefer', 'partnerRefer', 'planMapFlat', 'showInEligibleMsg', 'partnerReferProductNames', 'couponState', 'verifyCouponStatus', 'partnerMessageLoading', 'partnerFeaturesDependOnRules']),
    offerMsg() {
      return _.trimEnd(getMessageTemplateByRules(this.partnerRefer?.offerMsg)?.messageBody);
    },
    generalErrorMsg() {
      return _.trimEnd(getMessageTemplateByRules(this.partnerRefer?.generalErrorMsg)?.messageBody);
    },
    bindCouponRemovedErrorMessage() {
      return _.trimEnd(getMessageTemplateByRules(this.partnerRefer?.couponRemovedGeneralErrorMessage)?.messageBody);
    },
    partnerErrorMessage() {
      const removeCouponMessageSwitch = this.partnerRefer?.rules?.popUpModalForRemoveCoupon;
      const cartCouponCode = this.couponState?.couponCode;

      // need show related remove coupon error message with bind partner
      if (
        removeCouponMessageSwitch
        && this.partnerRefer?.couponCode
        && isEmptyVal(cartCouponCode)
        && this.verifyCouponStatus === couponFetchStatus.idle
        && isEmptyVal(this.couponState?.errorMessage)
      ) {
        return this.bindCouponRemovedErrorMessage;
      }

      return this.generalErrorMsg;
    },
    shouldShowSkeleton() {
      return this.partnerFeaturesDependOnRules?.usePlanCardMode;
    },
  },
  methods: {
    showOfferDetail() {
      try {
        this.partnerOfferDetailDialog.confirm();
        this.$tracking.buyInteraction(
          this.mx_trackingPartnerName,
          this.mx_trackingPartnerOfferDetailReason,
        );
      } catch (error) {
        logger.error('showOfferDetail error', error);
      }
    },
    renderEligibleIcon() {
      return <zm-badge class="opc-partner-refer__eligible--badge" text={this.$t('billing.partner.special_offer')} color="green" />
    },
    renderInEligibleIcon() {
      return <img class="opc-partner-refer__ineligible--minus" src={minusIconUrl} />;
    },
    renderOfferDetail() {
      if (this.partnerRefer?.features?.offerDetailBtnFromOpConfig) return null;
      return <zm-button
        data-testid="checkout-button-393"
        type="text"
        class="opc-partner-refer__offer-detail-cta"
        onClick={this.showOfferDetail}>
        {this.$t('billing.partner.offer_detail_cta')}
      </zm-button>;
    },
    renderEligibleTile() {
      return <div class="opc-partner-refer__eligible">
        {this.renderEligibleIcon()}
        <div class="opc-partner-refer__eligible--content">
          <span innerHTML={this.offerMsg} />{this.renderOfferDetail()}
        </div>
      </div>;
    },
    renderInEligibleTile() {
      return <div class="opc-partner-refer__ineligible">
        {this.renderInEligibleIcon()}
        <div class="opc-partner-refer__ineligible--content">
          <span role="alert" innerHTML={this.partnerErrorMessage} />{this.renderOfferDetail()}
        </div>
      </div>;
    },
    renderEligibleMessage(inEligible) {
      return inEligible ?
        this.renderInEligibleTile() :
        this.renderEligibleTile();
    },
    renderContent() {
      let contentNode = null;

      if (this.partnerMessageLoading) {
        contentNode = this.contentNode;
      } else {
        contentNode = this.contentNode = this.renderEligibleMessage(this.showInEligibleMsg);
      }

      return contentNode ?? this.renderEligibleMessage(this.showInEligibleMsg);
    }
  },
  render() {
    if (!this.isFromPartnerRefer) return;

    if (isEmptyVal(this.offerMsg) && isEmptyVal(this.generalErrorMsg)) return;

    const contentNode = this.renderContent();

    return <div class="opc-partner-refer">
      {
        this.shouldShowSkeleton ?
          <zm-skeleton loading={this.partnerMessageLoading} rows={3}>
            {contentNode}
          </zm-skeleton> :
          contentNode
      }
    </div>;

  },
  watch: {
    showInEligibleMsg: {
      immediate: true,
      handler() {
        const reason = this.mx_trackingPartnerImpressionReason;
        reason && this.$tracking.buyInteraction(this.mx_trackingPartnerName, this.mx_trackingPartnerImpressionReason);
      },
    },
  },
};
</script>

<style lang="scss">
@import '@/styles/_variables.scss';
@import '@zoom/billing-responsive/styles/responsive.scss';

.opc-partner-refer {
  margin-bottom: 16px;

  &__offer-detail-cta {
    &.zbo-button--text {
      text-transform: capitalize;
    }
  }

  &__eligible, &__ineligible {
    padding: 20px;
    border-radius: 12px;

    &--content {
      font-weight: 400;
      font-size: 14px;
      line-height: 18px;
      color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
    }

    @include media(mobile, tablet) {
      padding: 16px;
    }
  }

  &__eligible {
    display: flex;
    gap: 16px;
    align-items: center;
    position: relative;
    background: var(--zoom-color-bg-default, #FFFFFF);
    border: 1px solid var(--zoom-color-border-neutral, #939BA4);

    &--badge {
      white-space: nowrap;
    }

    @include media(mobile, tablet) {
      &:has(&--badge) {
        flex-wrap: wrap;
      }
    }
  }

  &__ineligible {
    position: relative;
    background: var(--zoom-color-inverse-neutral, #F7F9FA);

    &--minus {
      position: absolute;
      top: 22px;
      right: 22px;
      width: 16px;
      height: 16px;

      @include media(mobile, tablet) {
        top: 19px;
      }
    }
  }
}

.partner-refer-message-popup {
  .zbo-message-box {
    &__title {
      font-size: 24px !important;
      color: var(--zoom-color-text-stronger-neutral, #2A2B2D);

      span {
        font-weight: 400;
      }
    }
  }

  .zbo-button {
    &.zbo-button--primary, &.zbo-button--plain {
      font-size: 16px;
      line-height: 24px;
      letter-spacing: 0em;
      text-align: center;
    }

    &--plain {
      color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
    }
  }
}

</style>
